<template>
  <div class="streak-days" :class="{ mounted: mounted }">
    <div ref="solidElement" class="solid" />
    <ul ref="listElement">
      <li v-for="(day, index) in days" :key="index">
        <DialogStreakDaysItem
          :label="day"
          :unlocked="index <= currentCount"
          @update="handleUpdateSize"
        />
      </li>
    </ul>
  </div>
</template>
<script setup lang="ts">
import { gsap } from 'gsap';

import DialogStreakDaysItem from './DialogStreakDaysItem.vue';

interface Props {
  count: number;
  ready: boolean;
}

const props = defineProps({
  count: null,
  ready: { type: Boolean }
});

const emit = defineEmits(["complete", "start"]);

const mounted = useMounted();

const tl = gsap.timeline();

const now = useNow();

const widthTransform = ref<number>(0);

const currentCount = ref<number>(-1);

const solidElement = ref();

const listElement = ref();
const { width: widthList } = useElementSize(listElement);

const widthItem = ref<number>(0);

const handleUpdateSize = (val: number) => {
  widthItem.value = val;
};

const countDaysOfTheWeek = computed(() => {
  return (props.count - 1) % 7;
});

const days = computed(() => {
  const d = new Array(7).fill(0).map((item, index) => {
    const day = new Date(
      now.value.getTime() +
        (index - countDaysOfTheWeek.value) * 24 * 60 * 60 * 1000
    ).toLocaleDateString('fr-FR', { weekday: 'narrow' });
    return day;
  });
  return d;
});

const _update = (params) => {
  const _width = gsap.getProperty(solidElement.value, 'width') as number;

  widthTransform.value = _width;
};

const _startAnimation = () => {
  emit('start');
};

const _complete = () => {
  emit('complete');
};

watch(
  () => widthTransform.value,
  (w) => {
    if (w === 0) return;
    const spacing = (widthList.value - 7 * widthItem.value) / 6;
    const mid = spacing + widthItem.value / 2;
    const count = Math.floor(((w + mid) / widthList.value) * 7) - 1;

    currentCount.value = count;
  }
);

watch(
  () => {
    if (!widthItem.value || !widthList.value) return 0;
    return widthItem.value + widthList.value;
  },
  (val) => {
    if (val === 0) return;
    const spacing = (widthList.value - 7 * widthItem.value) / 6;
    const countUsed = countDaysOfTheWeek.value + 1;
    const x = countUsed * widthItem.value + spacing * (countUsed - 1);

    tl.to(
      solidElement.value,
      {
        width: x,
        ease: 'power4.out',
        duration: 1,
        onStart: _startAnimation,
        onUpdate: (params) => _update(params),
        onComplete: _complete,
        onUpdateParams: [solidElement.value, 'completed'],
      },
      0
    );
    tl.pause();
  }
);

watch(
  () => props.ready,
  (b) => {
    if (b) {
      tl.resume();
    }
  }
);
</script>
<style lang="scss" scoped>
.streak-days {
  position: relative;
}

ul {
  display: flex;
  position: relative;
  justify-content: space-between;
  width: 100%;
}

li {
  z-index: 1;
}

.solid {
  position: absolute;
  z-index: 0;
  top: 0;
  left: 0;
  height: 4.2rem;
  border-radius: 2.1rem; // depends of the size of items
  background-color: var(--color_secondary_lighter);
}
</style>
